import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Heart, Smile, MessageCircle } from "lucide-react";

const iconMap = { like: Heart, emoji: Smile, message: MessageCircle };

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setData(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("notifications load failed", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const onOpen = async (v) => {
    setOpen(v);
    if (v) {
      try { await api.post("/notifications/read"); } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("mark-read failed", e);
      }
      setTimeout(() => setData((d) => ({ ...d, unread: 0 })), 300);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-[hsl(var(--muted))] transition">
          <Bell className="w-5 h-5 text-[color:var(--umg-text-2)]" />
          {data.unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C85A53] text-white text-[10px] font-semibold grid place-items-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white dark:bg-neutral-900 border border-[#D6D2C9] dark:border-neutral-700">
        <div className="p-4 border-b border-[#D6D2C9] dark:border-neutral-700">
          <h4 className="font-display text-lg">Notifications</h4>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && (
            <div className="p-6 text-sm text-center text-[color:var(--umg-text-2)]">You're all caught up.</div>
          )}
          {data.items.map((n, i) => {
            const I = iconMap[n.kind] || Bell;
            const label = n.kind === "like" ? "liked your profile" : n.kind === "emoji" ? `sent ${n.emoji}` : "sent you a message";
            const key = n.id || n._id || `${n.from_id || "x"}-${n.kind}-${n.created_at || i}`;
            return (
              <Link
                key={key}
                to={n.kind === "message" ? `/messages/${n.from_id}` : `/profile/${n.from_id}`}
                onClick={() => setOpen(false)}
                data-testid={`notif-item-${i}`}
                className="flex items-start gap-3 p-3 border-b border-[#D6D2C9] dark:border-neutral-800 hover:bg-[hsl(var(--muted))] transition"
              >
                <div className="w-8 h-8 rounded-full bg-[#EAE6DF] dark:bg-neutral-800 grid place-items-center shrink-0">
                  <I className="w-4 h-4 text-[#C85A53]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm">
                    <span className="font-medium">{n.from_name || "Someone"}</span>{" "}
                    <span className="text-[color:var(--umg-text-2)]">{label}</span>
                  </div>
                  {n.text && <div className="text-xs text-[color:var(--umg-text-2)] truncate mt-0.5">{n.text}</div>}
                  <div className="text-[10px] text-[color:var(--umg-text-2)] mt-1">{new Date(n.at).toLocaleString()}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
